// client/src/pages/EditProduct/EditProduct.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./EditProduct.css";
import API_BASE_URL from "../../apiConfig";

const EditProduct = () => {
  const { sku } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    itemName: "",
    sku: "",
    qrCode: "",
    quantity: 0,
    location: "",
  });
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/inventory/${sku}`, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.message || "Error fetching product");
        }
        const data = await response.json();
        setFormData({
          itemName: data.itemName,
          sku: data.sku,
          qrCode: data.qrCode,
          quantity: data.quantity,
          location: data.location,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [sku]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "sku") {
      setFormData({
        ...formData,
        sku: value,
        qrCode: value,
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === "quantity" ? Number(value) : value,
      });
    }
  };

  const handleFileChange = (e) => {
    setImages(e.target.files);
  };

  const handleLocationSelect = (loc) => {
    setFormData({ ...formData, location: loc });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/inventory/${sku}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Error updating product");
      }
      await response.json();

      if (images.length > 0) {
        const formDataImages = new FormData();
        for (let i = 0; i < images.length; i++) {
          formDataImages.append("images", images[i]);
        }
        const imageResponse = await fetch(`${API_BASE_URL}/inventory/${sku}/images`, {
          method: "POST",
          credentials: "include",
          body: formDataImages,
        });
        if (!imageResponse.ok) {
          const errData = await imageResponse.json();
          throw new Error(errData.message || "Error uploading images");
        }
      }

      setSuccess("Product updated successfully!");
      setImages([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-product-page">
      <div className="container">
        <h1 className="page-title">Edit Product</h1>
        {loading && <p className="loading">Loading...</p>}
        {error && <p className="error-message">{error}</p>}
        {success && <p className="success-message">{success}</p>}
        <form onSubmit={handleSubmit} className="edit-product-form">
          <div className="form-group">
            <label htmlFor="itemName">Item Name</label>
            <input
              type="text"
              id="itemName"
              name="itemName"
              value={formData.itemName}
              onChange={handleChange}
              placeholder="Enter product name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="sku">SKU</label>
            <input
              type="text"
              id="sku"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              placeholder="Enter unique SKU"
              required
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="quantity">Quantity</label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Location</label>
            <div className="location-buttons">
              {["Biology", "Chemistry", "Electrical", "Mechanical"].map((loc) => (
                <button
                  key={loc}
                  type="button"
                  className={`location-btn ${formData.location === loc ? "active" : ""}`}
                  onClick={() => handleLocationSelect(loc)}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="images">Add Product Images (optional)</label>
            <input
              type="file"
              id="images"
              name="images"
              accept="image/*"
              multiple
              onChange={handleFileChange}
            />
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "Updating..." : "Update Product"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;